import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, IndicatorValue, Shapeable } from '@shapeable/types';
import { breakpoints } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { Topic } from '@shapeable/gesda-types';
import { ComponentPropsProvider, IndicatorGroupsRenderer, useIndicatorGroups } from '@shapeable/ui';
import { compact, maxBy } from 'lodash';
const cls = classNames('topic-psp-indicator-groups');

// -------- Types -------->

export type TopicPspIndicatorGroupsProps = Classable & HasChildren & {
  animate?: boolean;
  entity?: Topic;
  layoutComponent?: string;
};

export const TopicPspIndicatorGroupsDefaultProps: Omit<TopicPspIndicatorGroupsProps, 'entity'> = {
  layoutComponent: 'IndicatorGroupLayoutPspChart',
};

type TopicWithIndicatorValues = Topic & {
  indicatorValues?: IndicatorValue[];
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    
  `,
});

const GroupsStyles = breakpoints({
  base: css`
    
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Groups: styled(IndicatorGroupsRenderer)`${GroupsStyles}`,
};

const PSP_TINTS = [0, 0.32, 0.56];
const DEFAULT_COLOR = '#1BB1AB';

const hexToRgb = (color: string) => {
  const hex = color.replace('#', '');
  const value = hex.length === 3 ? hex.split('').map((char) => `${char}${char}`).join('') : hex;

  if (!/^[0-9A-Fa-f]{6}$/.test(value)) {
    return null;
  }

  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
};

const toHex = (value: number) => value.toString(16).padStart(2, '0');

const tintColor = (color: string, tint: number) => {
  const rgb = hexToRgb(color);

  if (!rgb) {
    return color;
  }

  const r = Math.round(rgb.r + ((255 - rgb.r) * tint));
  const g = Math.round(rgb.g + ((255 - rgb.g) * tint));
  const b = Math.round(rgb.b + ((255 - rgb.b) * tint));

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const TopicPspIndicatorGroups: Shapeable.FC<TopicPspIndicatorGroupsProps> = (props) => {
  const { animate, className, entity, layoutComponent } = props;
  const { indicatorValues = [] } = (entity || {}) as TopicWithIndicatorValues;
  const indicatorGroups = useIndicatorGroups();
  const baseColor = entity?.color?.value || entity?.trend?.color?.value || DEFAULT_COLOR;

  const items = compact(indicatorGroups
    .filter((group) => group.layout?.component === layoutComponent)
    .map((group) => ({
      ...group,
      indicators: (group.indicators || []).map((indicator, index) => {
        const indicatorValue = maxBy(
          indicatorValues.filter((item) => item.indicator?.id === indicator.id),
          'year',
        );

        return {
          ...indicator,
          color: {
            ...indicator.color,
            value: tintColor(baseColor, PSP_TINTS[index % PSP_TINTS.length]),
          },
          result: indicatorValue && {
            ...indicatorValue,
            indicator,
            value: indicatorValue.value === undefined && indicatorValue.numericValue !== undefined ? `${indicatorValue.numericValue}` : indicatorValue.value,
          },
        };
      }),
    })));

  if (!items.length) {
    return null;
  }

  const groups = (
    <My.Container className={cls.name(className)}>
      <My.Groups items={items} />
    </My.Container>
  );

  if (animate === undefined) {
    return groups;
  }

  return (
    <ComponentPropsProvider value={{
      IndicatorGroupLayoutPspChart: { animate },
    }}>
      {groups}
    </ComponentPropsProvider>
  );
};

TopicPspIndicatorGroups.defaultProps = TopicPspIndicatorGroupsDefaultProps;
TopicPspIndicatorGroups.cls = cls;
